import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Stack,
  Alert,
  CardActions,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
// Import our custom Grid component instead
import Grid from '../components/Grid';
import { experts } from '../data/experts';

const ExpertsPage: React.FC = () => {
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null,
  ) => {
    if (newFilter !== null) {
      setCategoryFilter(newFilter);
    }
  };

  const filteredExperts = categoryFilter === 'all'
    ? experts
    : experts.filter(expert => expert.category === categoryFilter);

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="secondary.main">
          Market Analysts Directory
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          A comprehensive listing of respected analysts and strategists in Gold and USD markets
        </Typography>
        <Alert severity="info">
          <strong>Note:</strong> The analysts listed in this directory are not affiliated with XAUJournal.
          We provide this directory as a resource to help you discover respected voices in the financial markets.
        </Alert>
      </Box>

      {/* Filter Controls */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'secondary.main' }}>Filter by:</Typography>
          <ToggleButtonGroup
            value={categoryFilter}
            exclusive
            onChange={handleFilterChange}
            aria-label="Expert category filter"
            size="small"
          >
            <ToggleButton value="all" aria-label="all analysts">
              All Analysts
            </ToggleButton>
            <ToggleButton value="independent" aria-label="independent analysts">
              Independent
            </ToggleButton>
            <ToggleButton value="institutional" aria-label="institutional analysts">
              Institutional
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {/* Experts Grid */}
      <Grid container spacing={3}>
        {filteredExperts.map(expert => (
          <Grid item key={expert.id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {expert.imageUrl && (
                <CardMedia
                  component="img"
                  height="160"
                  image={expert.imageUrl}
                  alt={expert.name}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" component="h2">
                  {expert.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {expert.role || expert.title}
                </Typography>
                {expert.organization && (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 1 }}>
                    {expert.organization}
                  </Typography>
                )}
                <Typography variant="body2">
                  {expert.shortBio || expert.bio?.substring(0, 120) + '...'}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-start', pl: 2, pb: 2, mt: 'auto' }}>
                <Button component={RouterLink} to={`/experts/${expert.id}`} size="small" color="primary">
                  View Profile
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No Results Message */}
      {filteredExperts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" fontStyle="italic">
            No analysts found with the selected filter.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ExpertsPage;